import { RefObject, useCallback, useEffect, useState } from 'react';
import { throttle } from 'lodash';

export interface UseScrollPositionOptions {
  threshold?: number;
  throttleMs?: number;
}

export interface UseScrollPositionReturn {
  showScrollToTop: boolean;
  showScrollToBottom: boolean;
}

interface ScrollState {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  isNearTop: boolean;
  isNearBottom: boolean;
}

/**
 * @param containerRef - Reference to the scrollable container element
 * @param options - Configuration options for threshold and throttling
 * @returns Object containing boolean flags for button visibility
 */
export const useScrollPosition = (
  containerRef: RefObject<HTMLElement | null>,
  options: UseScrollPositionOptions = {}
): UseScrollPositionReturn => {
  const { threshold = 200, throttleMs = 100 } = options;

  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    isNearTop: true,
    isNearBottom: true,
  });

  const updateScrollState = useCallback(
    (elem: HTMLElement | null) => {
      const container = elem;
      if (!container) {
        return;
      }

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      const isNearTop = scrollTop <= threshold;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

      setScrollState({
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearTop,
        isNearBottom,
      });
    },
    [threshold]
  );

  useEffect(() => {
    const throttledUpdateScrollState = throttle(() => updateScrollState(containerRef.current), throttleMs);

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleResize = () => {
      updateScrollState(container);
    };

    updateScrollState(container);

    container.addEventListener('scroll', throttledUpdateScrollState, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      container.removeEventListener('scroll', throttledUpdateScrollState);
      window.removeEventListener('resize', handleResize);
      throttledUpdateScrollState.cancel();
    };
  }, [containerRef, throttleMs, updateScrollState]);

  const showScrollToTop = !scrollState.isNearTop && scrollState.scrollHeight > scrollState.clientHeight;
  const showScrollToBottom = !scrollState.isNearBottom && scrollState.scrollHeight > scrollState.clientHeight;

  return {
    showScrollToTop,
    showScrollToBottom,
  };
};
